import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Button } from "components/common";
import { getPaymentRequest, resolvePaymentRequestBankUncertainty } from "redux/erp/paymentRequestSlice";
import UnderlineInput from "../common/UnderlineInput";

/**
 * Records "HSBC confirmed it did not receive the payment" in the unclear red box
 * (PRQ-202600013, 2026-10-23: HTTP 500, HSBC never had it). Same action as ERP & bank →
 * HSBCnet checked: the reason is kept on the request, then Send to HSBC again opens.
 */
const ConfirmNotReceivedButton = ({ paymentRequest: pr }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  if (!pr?.permissions?.allow_resolve_bank_uncertainty) return null;

  const save = async () => {
    if (busyRef.current || !reason.trim()) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await dispatch(resolvePaymentRequestBankUncertainty({ id: pr.id, reference: "", reason: reason.trim() })).unwrap();
      toast.success("Recorded — the payment can be sent to HSBC again.");
      setOpen(false);
    } catch (e) {
      toast.error(e?.error || e?.message || "Could not record HSBC's answer.");
    } finally {
      busyRef.current = false;
      setBusy(false);
      dispatch(getPaymentRequest(pr.id));
    }
  };

  if (!open) {
    return (
      <Button size="sm" variant="secondary" className="tw-mt-2" onClick={() => setOpen(true)}>
        <i className="bi bi-telephone-x" aria-hidden="true" /> HSBC confirmed not received
      </Button>
    );
  }
  return (
    <div className="tw-mt-2 tw-space-y-2 tw-text-geist-fg">
      <UnderlineInput aria-label="What HSBC reported" value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)}
        placeholder="Who at HSBC confirmed it, and when, e.g. HSBC support 23/10: not received" />
      <div className="tw-flex tw-gap-2">
        <Button size="sm" loading={busy} aria-busy={busy} disabled={!reason.trim()} onClick={save}>
          {busy ? "Saving…" : "Record not received"}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
};

ConfirmNotReceivedButton.propTypes = { paymentRequest: PropTypes.object };

export default ConfirmNotReceivedButton;
